import Drawer from '@components/Drawer/Drawer.component'
import { DELETE_PAYMENT_METHOD } from '@graphql/mutation/deletePaymentMethod.mutation'
import { GET_PAYMENT_METHODS } from '@graphql/queries/getPaymentMethods.query'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SavePaymentDropinContainer from '../../components/Dropin/SavePaymentDropinContainer'
import LoadingAnimation from '../../components/LoadingAnimation/LoadingAnimation.component'
import Notification from '../../components/Notification/Notification.component'
import { PaymentMethodItem } from '../../components/PaymentMethodItem/PaymentMethodItem.component'
import { useAuthenticatedMutation, useAuthenticatedQuery, useGetUser } from '../../hooks/useAuth'
import { NotificationType } from '../../types/notification.enum'
import {
    AddPaymentMethodWrapper,
    AddPaymentTitle,
    FullWrapper,
    NewPaymentMethodButtonOverride,
    NoPaymentMethodDescription,
    NoPaymentMethodIcon,
    NoPaymentMethodText,
    NoPaymentMethodWrapper,
    PaymentMethodAddIcon,
    Subtitle,
    Title
} from './AccountPayment.style'

const AccountPayment = () => {
    const { t } = useTranslation()
    const { loading } = useGetUser()

    const [paymentMethods, setPaymentMethods] = useState<any[]>([])
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null)
    const [showNotification, setShowNotification] = useState<NotificationType | null>(null)
    const [notificationMessageKey, setNotificationMessageKey] = useState<string | null>(null)
    const [isSavePaymentModalOpen, setIsSavePaymentModalOpen] = useState<boolean>(false)
    const [isDeletePaymentModalOpen, setIsDeletePaymentModalOpen] = useState<boolean>(false)
    const [isDeletePaymentLoading, setIsDeletePaymentLoading] = useState<boolean>(false)

    const {
        data,
        loading: paymentMethodsLoading,
        error,
        refetch: fetchPaymentMethods
    } = useAuthenticatedQuery({
        query: GET_PAYMENT_METHODS,
        fetchPolicy: 'no-cache'
    })

    const [deleteUserPaymentMethod] = useAuthenticatedMutation({
        mutation: DELETE_PAYMENT_METHOD
    })

    useEffect(() => {
        if (data && data.b2c_get_payment_methods && !error) {
            setPaymentMethods(data.b2c_get_payment_methods)
        }
    }, [data, setPaymentMethods])

    const onCloseNotification = () => {
        setShowNotification(null)
        setNotificationMessageKey(null)
    }

    const onNewPaymentMethodClick = () => {
        setIsSavePaymentModalOpen(true)
    }

    const onDeletePaymentMethodClick = (method: any) => {
        setIsDeletePaymentModalOpen(true)
        setSelectedPaymentMethod(method)
    }

    const showSuccessNotification = () => {
        setShowNotification(NotificationType.POSITIVE)
        setNotificationMessageKey('profile.successfulChanges')
    }

    const showErrorNotification = () => {
        setShowNotification(NotificationType.NEGATIVE)
        setNotificationMessageKey('profile.paymentSection.errorDeletingPaymentMethod')
    }

    const onSavePaymentSuccess = () => {
        fetchPaymentMethods()
        setIsSavePaymentModalOpen(false)
        showSuccessNotification()
    }

    const onSavePaymentError = () => {
        setIsSavePaymentModalOpen(false)
        showErrorNotification()
    }

    const onDeletePayment = async () => {
        try {
            setIsDeletePaymentLoading(true)
            await deleteUserPaymentMethod({
                variables: { payment_method_id: selectedPaymentMethod.id }
            })
            const updatedPaymentMethods = paymentMethods.filter((pm) => pm.id !== selectedPaymentMethod.id)
            setPaymentMethods(updatedPaymentMethods)
            showSuccessNotification()
        } catch (err) {
            console.log(err)
            showErrorNotification()
        } finally {
            setIsDeletePaymentLoading(false)
            setSelectedPaymentMethod(null)
            setIsDeletePaymentModalOpen(false)
        }
    }

    return (
        <FullWrapper>
            <Title>{t('profile.payment')}</Title>
            <Subtitle>{t('profile.paymentSection.subtitle')}</Subtitle>
            {(loading || paymentMethodsLoading) && <LoadingAnimation />}
            {!loading &&
                !paymentMethodsLoading &&
                paymentMethods.length > 0 &&
                paymentMethods.map((method) => (
                    <PaymentMethodItem
                        key={method.id}
                        variant={method.variant}
                        card={method.card_data}
                        onDelete={() => onDeletePaymentMethodClick(method)}
                    />
                ))}
            {!paymentMethodsLoading && paymentMethods.length === 0 && (
                <NoPaymentMethodWrapper>
                    <NoPaymentMethodIcon className="wemolo-card-1" />
                    <NoPaymentMethodText>{t('profile.paymentSection.noPaymentMethod')}</NoPaymentMethodText>
                    <NoPaymentMethodDescription>
                        {t('profile.paymentSection.noPaymentMethodDescription')}
                    </NoPaymentMethodDescription>
                </NoPaymentMethodWrapper>
            )}
            {!loading && (
                <AddPaymentMethodWrapper>
                    <Button
                        id="add-payment-method-btn"
                        onClick={onNewPaymentMethodClick}
                        $size={BUTTON_SIZE.medium}
                        $kind={BUTTON_KIND.primary}
                        overrides={NewPaymentMethodButtonOverride}
                    >
                        <PaymentMethodAddIcon className="wemolo-card-1" />
                        {t('profile.paymentSection.addPaymentMethod')}
                    </Button>
                </AddPaymentMethodWrapper>
            )}
            {showNotification && notificationMessageKey && (
                <Notification type={showNotification} onClose={onCloseNotification}>
                    {t(notificationMessageKey)}
                </Notification>
            )}
            <Drawer
                isOpen={isSavePaymentModalOpen}
                content={
                    <div>
                        <AddPaymentTitle>{t('profile.paymentSection.addPaymentMethod')}</AddPaymentTitle>
                        <SavePaymentDropinContainer
                            onCompleted={onSavePaymentSuccess}
                            onError={onSavePaymentError}
                            onPaymentMethodChange={() => {}}
                        />
                    </div>
                }
                onClose={() => setIsSavePaymentModalOpen(false)}
            />
            <Drawer
                isOpen={isDeletePaymentModalOpen}
                content={
                    <div>
                        <AddPaymentTitle>{t('profile.paymentSection.deletePaymentMethod')}</AddPaymentTitle>
                        {/* maybe makes sense to display which payment method we want to delete here? */}
                        <Button
                            id="delete-payment-method-btn"
                            isLoading={isDeletePaymentLoading}
                            onClick={onDeletePayment}
                            $size={BUTTON_SIZE.medium}
                            $kind={BUTTON_KIND.primary}
                            style={{ width: '100%', marginTop: '15px' }}
                        >
                            {t('profile.paymentSection.confirmDeletePaymentMethod')}
                        </Button>
                    </div>
                }
                onClose={() => {
                    setIsDeletePaymentModalOpen(false)
                    setSelectedPaymentMethod(null)
                }}
            />
        </FullWrapper>
    )
}

export default AccountPayment
